import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { duskColor } from './DayNight';

// Each climate is a mix of a few ingredients (0..1). The scene blends smoothly between mixes.
// Sky clouds (drawn by CloudSky.jsx):
//   cover   – how much of the sky is covered (0..1)
//   dark    – 0 white fluffy … 1 dark storm clouds
//   replace – 0 keeps the photo sky of a sunny day, 1 swaps it for the climate's own procedural sky
//   wind    – also drives how fast the clouds race and how stretched/streaky they are
export const CLIMATES = {
  clear:  { label: 'Clear',  icon: '☀️', mix: { cloud: 0,    rain: 0, snow: 0, fog: 0,    storm: 0, wind: 0,    cover: 0,    dark: 0,    replace: 0 } },
  cloudy: { label: 'Cloudy', icon: '☁️', mix: { cloud: 0.75, rain: 0, snow: 0, fog: 0.15, storm: 0, wind: 0.2,  cover: 0.8,  dark: 0.35, replace: 1 } },
  windy:  { label: 'Windy',  icon: '🍃', mix: { cloud: 0.3,  rain: 0, snow: 0, fog: 0,    storm: 0, wind: 1,    cover: 0.62, dark: 0.08, replace: 1 } },
  rain:   { label: 'Rain',   icon: '🌧️', mix: { cloud: 0.9,  rain: 1, snow: 0, fog: 0.3,  storm: 0, wind: 0.25, cover: 0.95, dark: 0.6,  replace: 1 } },
  storm:  { label: 'Storm',  icon: '⛈️', mix: { cloud: 1,    rain: 1, snow: 0, fog: 0.4,  storm: 1, wind: 0.8,  cover: 1,    dark: 0.9,  replace: 1 } },
  snow:   { label: 'Snow',   icon: '❄️', mix: { cloud: 0.7,  rain: 0, snow: 1, fog: 0.35, storm: 0, wind: 0.1,  cover: 0.9,  dark: 0.2,  replace: 1 } },
  fog:    { label: 'Fog',    icon: '🌫️', mix: { cloud: 0.5,  rain: 0, snow: 0, fog: 1,    storm: 0, wind: 0,    cover: 0.6,  dark: 0.15, replace: 1 } },
};
export const CLIMATE_IDS = Object.keys(CLIMATES);

const KEYS = ['cloud', 'rain', 'snow', 'fog', 'storm', 'wind', 'cover', 'dark', 'replace'];
// Wind direction (where it blows TOWARDS, as a unit vector in the ground plane: +x east, −z north).
// Clouds, rain, snow, leaves and trees all follow it. It starts random and is replaced by the
// visitor's real wind when their local weather arrives (see setWindFrom); changes ease in smoothly.
export const createWeatherState = (climate = 'clear') => {
  const a = Math.random() * Math.PI * 2;
  const dir = { windX: Math.cos(a), windZ: Math.sin(a) };
  return { ...CLIMATES[climate].mix, flash: 0, strikes: 0, ...dir, windToX: dir.windX, windToZ: dir.windZ };
};

// degreesFrom: meteorological wind direction (where the wind comes FROM, clockwise from north).
export const setWindFrom = (weatherRef, degreesFrom) => {
  if (!weatherRef?.current || !Number.isFinite(degreesFrom)) return;
  const b = (degreesFrom * Math.PI) / 180;
  // blowing towards the opposite bearing; bearing b points to (sin b, −cos b) in our world
  weatherRef.current.windToX = -Math.sin(b);
  weatherRef.current.windToZ = Math.cos(b);
};

// Phones get fewer particles.
const IS_MOBILE = typeof window !== 'undefined' && (window.innerWidth < 768 || /iPhone|iPad|Android/i.test(navigator.userAgent));

// --- Hall geometry (matches gallerySettings.walls and the roof lantern) ---------------
// Weather happens OUTSIDE the hall only. Inside its footprint, particles stop on the glass roof.
const HALL_HALF = 12.3;       // walls are at ±12 (plus thickness)
const ROOF_BASE = 6;          // wall height
const ROOF_TOP = 8;           // lantern top
const ROOF_TOP_HALF = 8.5;    // half width of the flat top
export const roofHeightAt = (x, z) => {
  // Covered passage behind the glass door on the south wall (see GlassDoor.jsx).
  if (Math.abs(x) < 1.8 && z > 11.9 && z < 17.6) return 3.75;
  const d = Math.max(Math.abs(x), Math.abs(z));
  if (d > HALL_HALF) return 0; // outside the hall: fall to the ground
  if (d <= ROOF_TOP_HALF) return ROOF_TOP + 0.05;
  return ROOF_BASE + (ROOF_TOP - ROOF_BASE) * (HALL_HALF - d) / (HALL_HALF - ROOF_TOP_HALF) + 0.05;
};

// Leaves may fly low outside (visible through the windows) but stay above the roof over the hall.
const minLeafY = (x, z) => (Math.abs(x) < 13.5 && Math.abs(z) < 13.5 ? 9 : 1.5);

const BLEND_SPEED = 0.35; // a full change takes about 3 seconds

// Blends the weather ingredients and drives lightning and the distant fog.
export const WeatherDriver = ({ climate, weatherRef, nightRef, skyRef }) => {
  const { scene } = useThree();
  const nextFlashAt = useRef(0);
  // Linear fog that only starts beyond the hall, so the inside always stays clear.
  const fog = useMemo(() => new THREE.Fog('#c9ced6', 40, 100000), []);
  const dayFog = useMemo(() => new THREE.Color('#c9ced6'), []);
  const nightFog = useMemo(() => new THREE.Color('#0c111d'), []);
  const duskFog = useMemo(() => new THREE.Color(), []);

  useEffect(() => {
    scene.fog = fog; // set once; changing fog on/off later would force shader recompiles
    return () => { scene.fog = null; };
  }, [scene, fog]);

  useFrame((state, delta) => {
    const w = weatherRef.current;
    const target = (CLIMATES[climate] || CLIMATES.clear).mix;
    const step = Math.min(delta * BLEND_SPEED, 1);
    KEYS.forEach((k) => {
      const diff = target[k] - w[k];
      w[k] = Math.abs(diff) < 0.001 ? target[k] : w[k] + Math.sign(diff) * Math.min(Math.abs(diff), step);
    });

    // Turn the wind direction smoothly towards its target (about 3 s for a full turn).
    const k = Math.min(1, delta * 1.2);
    w.windX += (w.windToX - w.windX) * k;
    w.windZ += (w.windToZ - w.windZ) * k;
    const len = Math.hypot(w.windX, w.windZ) || 1;
    w.windX /= len; w.windZ /= len;

    // Lightning: random double flashes during storms.
    const t = state.clock.elapsedTime;
    if (w.storm > 0.5 && t > nextFlashAt.current) {
      w.flash = 1;
      w.strikes = (w.strikes || 0) + 1; // lets the thunder sound react to every strike
      nextFlashAt.current = t + 3 + Math.random() * 7;
    }
    w.flash = Math.max(0, w.flash - delta * 4);
    if (w.flash > 0 && w.flash < 0.6 && Math.random() < 0.04) w.flash = 0.8; // flicker

    // Clouds and storms dim the sky's reflected light (DayNightDriver sets the base value each frame).
    scene.environmentIntensity *= 1 - w.cloud * 0.4 - w.storm * 0.15;

    const n = nightRef ? nightRef.current : 0;
    fog.color.copy(dayFog);
    if (skyRef && skyRef.current.dusk > 0) fog.color.lerp(duskColor(skyRef.current, duskFog), skyRef.current.dusk * 0.45); // warm haze at sunrise / sunset
    fog.color.lerp(nightFog, n).multiplyScalar(1 - w.dark * 0.6);
    const thickness = Math.min(1, w.fog + w.rain * 0.3 + w.snow * 0.3);
    fog.near = 40;
    // Clear days keep a light atmospheric haze (distant mountains fade softly); thick fog closes in.
    fog.far = 140 + Math.pow(1 - thickness, 4) * 900;
  });

  return null;
};

// NOTE on materials: weather outside is seen THROUGH the glass roof. The roof uses transmission,
// which only shows opaque objects, so particles use transparent:false with additive blending
// (or alphaTest for leaves). Inactive effects draw nothing (drawRange / count = 0) instead of
// toggling visibility, so no shader ever needs compiling in the middle of a visit.

// Rain streaks falling outside and onto the glass roof.
export const Rain = ({ weatherRef }) => {
  const count = IS_MOBILE ? 2500 : 5000;
  const matRef = useRef();
  const { geometry, speeds } = useMemo(() => {
    const pos = new Float32Array(count * 6);
    const spd = new Float32Array(count);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const data = { geometry: geo, speeds: spd };
    for (let i = 0; i < count; i++) respawnDrop(data, i, Math.random() * 30);
    geo.setDrawRange(0, 0);
    return data;
  }, [count]);

  useFrame((_, delta) => {
    const w = weatherRef.current;
    const amount = w.rain;
    if (matRef.current) matRef.current.opacity = amount * 0.8;
    const active = Math.round(count * Math.min(1, amount * 1.2));
    geometry.setDrawRange(0, active * 2);
    if (active === 0) return;
    const pos = geometry.attributes.position.array;
    const dt = Math.min(delta, 0.05);
    const drift = 0.08 + w.wind * 0.5;
    for (let i = 0; i < active; i++) {
      const o = i * 6;
      const dy = speeds[i] * dt;
      pos[o + 1] -= dy; pos[o + 4] -= dy;
      pos[o] += dy * drift * w.windX; pos[o + 3] += dy * drift * w.windX;
      pos[o + 2] += dy * drift * w.windZ; pos[o + 5] += dy * drift * w.windZ;
      // Check at the drop's current position: wind drift can carry a drop from outside the wall
      // over the hall, where it must stop on the glass roof (or the wall) instead of falling inside.
      if (pos[o + 4] < roofHeightAt(pos[o + 3], pos[o + 5])) respawnDrop({ geometry, speeds }, i, 26 + Math.random() * 6, w);
    }
    geometry.attributes.position.needsUpdate = true;
  });

  return (
    <lineSegments geometry={geometry} frustumCulled={false}>
      <lineBasicMaterial ref={matRef} color="#c8d8ea" transparent={false} opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} />
    </lineSegments>
  );
};

// The streak leans downwind (more in strong wind): its lower end is offset along the wind.
function respawnDrop({ geometry, speeds }, i, y, w) {
  const pos = geometry.attributes.position.array;
  const x = (Math.random() - 0.5) * 70, z = (Math.random() - 0.5) * 70;
  const lean = w ? 0.05 + w.wind * 0.25 : 0.05;
  const o = i * 6;
  pos[o] = x; pos[o + 1] = y; pos[o + 2] = z;
  pos[o + 3] = x + lean * (w ? w.windX : 1); pos[o + 4] = y - 0.6; pos[o + 5] = z + lean * (w ? w.windZ : 0);
  speeds[i] = 16 + Math.random() * 8;
}

const makeFlakeTexture = () => {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.8)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
};

// Snowflakes drifting outside and settling on the roof.
export const Snow = ({ weatherRef }) => {
  const count = IS_MOBILE ? 1500 : 3000;
  const matRef = useRef();
  const texture = useMemo(makeFlakeTexture, []);
  const { geometry, seeds } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const sd = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const x = (Math.random() - 0.5) * 60, z = (Math.random() - 0.5) * 60;
      pos.set([x, Math.random() * 25, z], i * 3);
      sd[i] = Math.random() * 100;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setDrawRange(0, 0);
    return { geometry: geo, seeds: sd };
  }, [count]);

  useFrame((state, delta) => {
    const w = weatherRef.current;
    const amount = w.snow;
    if (matRef.current) matRef.current.opacity = amount;
    const active = Math.round(count * Math.min(1, amount * 1.2));
    geometry.setDrawRange(0, active);
    if (active === 0) return;
    const pos = geometry.attributes.position.array;
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    for (let i = 0; i < active; i++) {
      const o = i * 3, s = seeds[i];
      pos[o + 1] -= (0.9 + (s % 1) * 0.9) * dt;
      pos[o] += (Math.sin(t * 0.6 + s) * 0.4 + w.wind * 2 * w.windX) * dt;
      pos[o + 2] += (Math.cos(t * 0.5 + s) * 0.4 + w.wind * 2 * w.windZ) * dt;
      if (pos[o + 1] < roofHeightAt(pos[o], pos[o + 2])) { // current position, see Rain
        const x = (Math.random() - 0.5) * 60, z = (Math.random() - 0.5) * 60;
        pos[o] = x; pos[o + 1] = 24; pos[o + 2] = z;
      }
    }
    geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points geometry={geometry} frustumCulled={false}>
      <pointsMaterial ref={matRef} map={texture} size={0.22} sizeAttenuation transparent={false} opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} color="#ffffff" />
    </points>
  );
};

// --- Wind: leaves tumbling over the glass roof -----------------------------------------
const makeLeafTexture = () => {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(32, 2);
  ctx.bezierCurveTo(58, 18, 54, 46, 32, 62);
  ctx.bezierCurveTo(10, 46, 6, 18, 32, 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'; // central vein
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(32, 6); ctx.lineTo(32, 60); ctx.stroke();
  return new THREE.CanvasTexture(c);
};
const LEAF_COLORS = ['#c0562a', '#d98e2b', '#8a3b1c', '#e0b03a', '#6f8f2f', '#a8471f'].map((c) => new THREE.Color(c));

export const Leaves = ({ weatherRef, nightRef }) => {
  const count = IS_MOBILE ? 120 : 240;
  const meshRef = useRef();
  const matRef = useRef();
  const texture = useMemo(makeLeafTexture, []);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const leaves = useMemo(
    () =>
      Array.from({ length: count }, () => ({
        x: (Math.random() - 0.5) * 70,
        y: 2 + Math.random() * 15, // kept out of the hall below (see minLeafY)
        z: (Math.random() - 0.5) * 70,
        speed: 4 + Math.random() * 5,
        spin: new THREE.Vector3(Math.random() * 4, Math.random() * 4, Math.random() * 4),
        rot: new THREE.Euler(Math.random() * 6, Math.random() * 6, Math.random() * 6),
        phase: Math.random() * 100,
        size: 0.55 + Math.random() * 0.45,
      })),
    [count]
  );

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    leaves.forEach((_, i) => mesh.setColorAt(i, LEAF_COLORS[i % LEAF_COLORS.length]));
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = 0;
  }, [leaves]);

  useFrame((state, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const { wind, windX, windZ } = weatherRef.current;
    // Unlit material (seen from below against the sky, lit leaves look black); dim it at night.
    if (matRef.current) matRef.current.color.setScalar(1 - 0.75 * (nightRef ? nightRef.current : 0));
    const active = Math.round(count * Math.min(1, wind * 1.1));
    mesh.count = active;
    if (active === 0) return;
    const t = state.clock.elapsedTime;
    const dt = Math.min(delta, 0.05);
    for (let i = 0; i < active; i++) {
      const l = leaves[i];
      l.x += l.speed * (0.4 + wind) * dt;
      l.y += Math.sin(t * 1.3 + l.phase) * 1.2 * dt;
      l.z += Math.cos(t * 0.7 + l.phase) * 1.5 * dt;
      if (l.x > 35) { l.x = -35; l.z = (Math.random() - 0.5) * 70; l.y = 2 + Math.random() * 15; }
      // l.x runs along the wind and l.z across it: turn into world space so leaves fly with the wind
      const wx = l.x * windX - l.z * windZ, wz = l.x * windZ + l.z * windX;
      l.y = Math.max(minLeafY(wx, wz), Math.min(18, l.y));
      l.rot.x += l.spin.x * dt; l.rot.y += l.spin.y * dt; l.rot.z += l.spin.z * dt;
      dummy.position.set(wx, l.y, wz);
      dummy.rotation.copy(l.rot);
      dummy.scale.setScalar(l.size);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[null, null, count]} frustumCulled={false}>
      <planeGeometry args={[1, 1]} />
      <meshBasicMaterial ref={matRef} map={texture} alphaTest={0.5} side={THREE.DoubleSide} />
    </instancedMesh>
  );
};

// --- Raindrops on the glass roof ---------------------------------------------------------
// A transparent overlay just under each roof panel. Drops splatter, then roll down the slope
// leaving thin trails. Seen from inside the hall, directly (not through transmission).
const glassVertex = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const glassFragment = /* glsl */ `
  uniform float uTime;
  uniform float uAmount;
  uniform vec2 uSize;     // panel size in metres (keeps drops round)
  uniform float uSlope;   // 1 = sloped panel (drops roll), 0 = flat top
  varying vec2 vUv;

  float hash1(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  vec2 hash2(vec2 p) { float n = hash1(p); return vec2(n, hash1(p + n)); }

  // Shades one drop like a tiny lens. Returns (dark, bright) contributions.
  //   dark: refracted rim + slight tint of the body, bright: specular highlight + focused light.
  vec2 lens(vec2 v, float r) {
    float d = length(v) / r;
    float aa = fwidth(d) * 1.5 + 0.02;                 // anti-aliasing, so small drops don't flicker
    float cover = 1.0 - smoothstep(1.0 - aa, 1.0, d);
    float rim = smoothstep(0.55, 1.0, d) * cover;
    float spec = (1.0 - smoothstep(0.0, 0.3, length(v / r - vec2(-0.32, 0.38)))) * cover;
    float focus = (1.0 - smoothstep(0.1, 0.75, length(v / r - vec2(0.15, -0.35)))) * cover;
    return vec2(rim * 0.7 + cover * 0.12, spec * 0.95 + focus * 0.25);
  }

  // Drops that land, splash, sit, and slowly evaporate before another lands in the same spot.
  vec2 staticDrops(vec2 p, float cell, float seed, float density) {
    vec2 g = p / cell;
    vec2 id = floor(g);
    vec2 h = hash2(id + seed);
    if (h.x > density) return vec2(0.0);
    vec2 v = (fract(g) - 0.5 - (hash2(id + seed + 9.1) - 0.5) * 0.6) * cell;
    float life = fract(uTime * (0.05 + 0.08 * h.y) + h.x * 7.0);
    float r = cell * (0.1 + 0.16 * hash1(id + seed + 3.1)) * (1.0 - 0.45 * life);
    float fade = smoothstep(0.0, 0.02, life) * (1.0 - smoothstep(0.75, 1.0, life));
    vec2 c = lens(v, r) * fade;
    // Impact splash: a thin ring that expands and fades in the first moments.
    float ring = smoothstep(0.06, 0.0, life) * smoothstep(r * 0.25, 0.0, abs(length(v) - r * (1.0 + life * 30.0)));
    return c + vec2(0.0, ring * 0.6);
  }

  // Drops rolling down the slope with a stop-start motion and a trail of tiny droplets.
  vec2 rollingDrops(vec2 p) {
    float colW = 0.42;
    float col = floor(p.x / colW);
    float hx = hash1(vec2(col, 7.0));
    if (hx < 0.45) return vec2(0.0);
    float k = fract(uTime * (0.025 + 0.045 * hash1(vec2(col, 9.0))) + hx * 10.0);
    float prog = k - 0.015 * sin(6.2831 * k * 8.0);   // speeds up and slows down (never reverses)
    float len = uSize.y + 0.4;
    float headY = len * (1.0 - prog) - 0.2;          // travels towards the lower edge (y = 0)
    // Wavy path, shared by the drop and its trail.
    float x = (fract(p.x / colW) - 0.5) * colW;
    x += sin(p.y * 2.3 + hx * 20.0) * 0.045 + sin(p.y * 7.1 + hx * 3.0) * 0.012;
    float dy = p.y - headY;
    vec2 c = lens(vec2(x, dy * 0.8), 0.05);
    // Trail above the drop: small left-behind droplets plus a faint wet streak.
    float trailLen = 1.1;
    float t = clamp(dy / trailLen, 0.0, 1.0);
    float inTrail = step(0.0, dy) * (1.0 - t);
    float cellY = 0.055;
    float tid = floor(p.y / cellY);
    float present = step(0.45, hash1(vec2(col, tid)));
    float tr = (0.008 + 0.01 * hash1(vec2(tid, col))) * (1.0 - t * 0.6);
    c += lens(vec2(x, (fract(p.y / cellY) - 0.5) * cellY), tr) * present * inTrail;
    c.x += smoothstep(0.012, 0.0, abs(x)) * inTrail * 0.18;
    return c;
  }

  void main() {
    vec2 p = vUv * uSize;
    vec2 c = staticDrops(p, 0.16, 1.0, 0.55)
           + staticDrops(p + 3.7, 0.09, 5.0, 0.45) * 0.8
           + staticDrops(p + 9.2, 0.32, 9.0, 0.35);
    c += rollingDrops(p) * uSlope;
    float dark = clamp(c.x, 0.0, 1.0);
    float bright = clamp(c.y, 0.0, 1.0);
    float alpha = clamp(dark * 0.55 + bright + 0.035, 0.0, 1.0) * uAmount; // + faint wet sheen
    vec3 col = mix(vec3(0.08, 0.11, 0.16), vec3(1.0), bright / (dark * 0.55 + bright + 1e-4));
    gl_FragColor = vec4(col, alpha);
  }
`;

const RoofRainPanel = ({ corners, sloped, weatherRef }) => {
  const { geometry, material } = useMemo(() => {
    // Slightly below the glass so it is drawn on the inside surface.
    const c = corners.map((v) => v.clone().add(new THREE.Vector3(0, -0.03, 0)));
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(c.flatMap((v) => [v.x, v.y, v.z])), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const width = c[0].distanceTo(c[1]);
    const height = c[0].distanceTo(c[3]);
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uAmount: { value: 0 },
        uSize: { value: new THREE.Vector2(width, height) },
        uSlope: { value: sloped ? 1 : 0 },
      },
      vertexShader: glassVertex,
      fragmentShader: glassFragment,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    return { geometry: geo, material: mat };
  }, [corners, sloped]);

  useFrame((state) => {
    const w = weatherRef.current;
    material.uniforms.uTime.value = state.clock.elapsedTime;
    material.uniforms.uAmount.value = w.rain; // only rain splatters on the glass
  });

  return <mesh geometry={geometry} material={material} renderOrder={3} />;
};

// `panels`: [{ corners: [Vector3 x4, lower edge first], sloped: bool }]
export const RoofRain = ({ panels, weatherRef }) => (
  <>
    {panels.map((p, i) => (
      <RoofRainPanel key={i} corners={p.corners} sloped={p.sloped} weatherRef={weatherRef} />
    ))}
  </>
);

// --- Climate choice persistence ------------------------------------------------
export const getSavedClimateMode = () => {
  try {
    const v = localStorage.getItem('gallery-climate');
    if (v === 'auto' || CLIMATES[v]) return v;
  } catch (e) { /* storage unavailable */ }
  return 'auto';
};
export const saveClimateMode = (mode) => {
  try { localStorage.setItem('gallery-climate', mode); } catch (e) { /* ignore */ }
};

// Next climate for auto mode: never the same twice in a row, clear skies a bit more often.
const AUTO_WEIGHTS = { clear: 3, cloudy: 2, windy: 2, rain: 2, storm: 1, snow: 2, fog: 1 };
// `recent`: the last climates shown (most recent first); none of them is picked again yet.
export const pickNextClimate = (recent = []) => {
  const avoid = Array.isArray(recent) ? recent : [recent];
  let options = CLIMATE_IDS.filter((c) => !avoid.includes(c));
  if (!options.length) options = CLIMATE_IDS;
  const total = options.reduce((s, c) => s + AUTO_WEIGHTS[c], 0);
  let r = Math.random() * total;
  for (const c of options) {
    r -= AUTO_WEIGHTS[c];
    if (r <= 0) return c;
  }
  return options[0];
};

// First climate in auto mode: random, but a pleasant one is a bit more likely.
export const pickInitialClimate = () => pickNextClimate([]);

// How long a climate lasts in auto mode: random, so changes never come in a fixed rhythm.
export const randomClimateDuration = (minMs, maxMs) => minMs + Math.random() * (maxMs - minMs);
